import React from 'react';
import { styled } from '@compiled/react';
import InvalidIcon from '@atlaskit/icon/core/error';
import InfoIcon from '@atlaskit/icon/core/migration/information--editor-panel';
import InvalidIconOld from '@atlaskit/icon/glyph/jira/failed-build-status';
import { Inline, xcss } from '@atlaskit/primitives';
import { components, CheckboxOption, type OptionProps } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import type { SelectOption } from '../../../../common/types.tsx';
import { CustomOptionStyles } from '../../../../common/ui/picker/styled.tsx';
import { type OptionState, getOptionState } from './utils.tsx';

type OptionTypeSingleAndMultiSelect = OptionProps<SelectOption, true> & { isMulti?: boolean };

export const Option = ({ isMulti = true, ...props }: OptionTypeSingleAndMultiSelect) => {
	const { isOnlyUnselectable, disabledMessage, invalid } =
		props.data?.optionType === 'option' ||
		props.data?.optionType === 'avatar' ||
		props.data?.optionType === 'lozenge' ||
		props.data?.optionType === 'field'
			? props.data
			: {
					isOnlyUnselectable: undefined,
					disabledMessage: undefined,
					invalid: undefined,
				};

	// Only disable checkbox for an only unselectable option when it is not a selected option
	const optionState = getOptionState(
		props.isDisabled,
		Boolean(isOnlyUnselectable),
		props.isSelected,
		Boolean(invalid),
	);

	const hasIconAfter = () => {
		switch (optionState) {
			case 'only-unselectable-unselected':
			case 'only-unselectable-selected':
			case 'disabled':
				return Boolean(disabledMessage);
			case 'invalid':
				return true;
			default:
				return undefined;
		}
	};

	const OptionInner = (
		<Inline
			spread="space-between"
			alignBlock="center"
			xcss={optionStateAttributes[optionState].xcss}
		>
			<CustomOptionStyles
				hasIconAfter={hasIconAfter()}
				isFocused={props.isFocused}
				isDisabled={props.isDisabled}
				isSingleSelect
			>
				{isMulti ? (
					/* @ts-expect-error - This component has stricter OptionType args which isn't compatible with ErrorOption in SelectOption. */
					<CheckboxOption {...props} />
				) : (
					<components.Option {...props} isMulti={false} />
				)}
			</CustomOptionStyles>
			<TooltipContainer>
				{(optionState === 'only-unselectable-unselected' ||
					optionState === 'only-unselectable-selected' ||
					optionState === 'disabled') &&
					Boolean(disabledMessage) && (
						<Tooltip content={disabledMessage}>
							<InfoIcon
								testId="jql-builder-basic-picker.ui.async-select.select.option.info-icon"
								label=""
								spacing="spacious"
								color={token('color.icon')}
								LEGACY_primaryColor={token('color.icon')}
							/>
						</Tooltip>
					)}
				{optionState === 'invalid' && (
					<Tooltip content={props.selectProps.invalidLabel}>
						<InvalidIcon
							testId="jql-builder-basic-picker.ui.async-select.select.option.invalid-icon"
							label=""
							spacing="spacious"
							color={token('color.icon.danger')}
							LEGACY_fallbackIcon={InvalidIconOld}
						/>
					</Tooltip>
				)}
			</TooltipContainer>
		</Inline>
	);
	return (
		<div data-testid={optionStateAttributes[optionState].testid}>
			{optionState === 'only-unselectable-unselected' ||
			optionState === 'disabled' ||
			optionState === 'invalid' ? (
				<UnsafeStyledCheckboxOption>{OptionInner}</UnsafeStyledCheckboxOption>
			) : (
				OptionInner
			)}
		</div>
	);
};

export default Option;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsafeStyledCheckboxOption = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& > div > div > div': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		backgroundColor: 'inherit !important',
	},
});

// These styles allow for the tooltip to sit over the option, so that the
// option width for the hover state is consistent with and without a tooltip
// This is a work-around that will need to have a more robust long-term solution
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const TooltipContainer = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginLeft: '-100px',
	paddingRight: token('space.150'),
	height: '24px',
});

const DisabledCheckboxOptionStyles = xcss({
	color: 'color.text.disabled',
});

const InvalidCheckboxOptionStyles = xcss({
	backgroundColor: 'color.background.danger',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const optionStateAttributes: Record<
	OptionState,
	{
		testid: string;
		xcss: ReturnType<typeof xcss>[];
	}
> = {
	disabled: {
		testid: 'jql-builder-basic.ui.jql-editor.picker.async-select.select.option.disabled-option',
		xcss: [DisabledCheckboxOptionStyles],
	},
	invalid: {
		testid: 'jql-builder-basic.ui.jql-editor.picker.async-select.select.option.invalid-option',
		xcss: [InvalidCheckboxOptionStyles],
	},
	selected: {
		testid: 'jql-builder-basic.ui.jql-editor.picker.async-select.select.selected-option',
		xcss: [],
	},
	unselected: {
		testid: 'jql-builder-basic.ui.jql-editor.picker.async-select.select.option',
		xcss: [],
	},
	'only-unselectable-unselected': {
		testid: 'jql-builder-basic.ui.jql-editor.picker.async-select.select.option.only-unselectable',
		xcss: [DisabledCheckboxOptionStyles],
	},
	'only-unselectable-selected': {
		testid:
			'jql-builder-basic.ui.jql-editor.picker.async-select.select.selected-option.only-unselectable',
		xcss: [],
	},
};
